import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";

const API = "/api/gold";

export default function GoldBacktest() {
  const [mode, setMode] = useState("scan");
  const [symbol, setSymbol] = useState("XAUTUSDT");
  const [interval, setInterval_] = useState("1h");
  const [days, setDays] = useState("90");
  const [strategy, setStrategy] = useState("ema-crossover");
  const [target, setTarget] = useState("2");
  const [slAtr, setSlAtr] = useState("1.5");
  const [qty, setQty] = useState("0.01");
  const [leverage, setLeverage] = useState("1");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  const DEFAULT_SCANS = [
    {id:1, symbol:"XAUTUSDT", interval:"4h", strategy:"mtf-ema", target:"3", slAtr:"1.5"},
    {id:2, symbol:"XAUTUSDT", interval:"4h", strategy:"ema-crossover", target:"2.5", slAtr:"1.5"},
    {id:3, symbol:"XAUTUSDT", interval:"1h", strategy:"donchian-breakout", target:"2.5", slAtr:"1.5"},
    {id:4, symbol:"XAUTUSDT", interval:"1h", strategy:"bb-squeeze", target:"2", slAtr:"1.5"},
  ];
  const [scanConfigs, setScanConfigs] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem("gold_scan_configs") || "null");
      return saved && saved.length > 0 ? saved : DEFAULT_SCANS;
    } catch { return DEFAULT_SCANS; }
  });
  const [scanningAll, setScanningAll] = useState(true);
  const [scanResults, setScanResults] = useState({});
  const [paperTrades, setPaperTrades] = useState(() => {
    try { return JSON.parse(localStorage.getItem("gold_paper_trades") || "[]"); } catch { return []; }
  });
  const [autoTrade, setAutoTrade] = useState(true);
  const scanTimersRef = useRef({});
  const processedRef = useRef(new Set());
  const ltpCacheRef = useRef({});

  useEffect(() => { localStorage.setItem("gold_paper_trades", JSON.stringify(paperTrades)); }, [paperTrades]);
  useEffect(() => { localStorage.setItem("gold_scan_configs", JSON.stringify(scanConfigs)); }, [scanConfigs]);

  function addScanConfig() {
    setScanConfigs(prev => [...prev, {
      id: Date.now(), symbol: "XAUTUSDT", interval: "4h", strategy: "mtf-ema", target: "3", slAtr: "1.5",
    }]);
  }
  function removeScanConfig(id) {
    setScanConfigs(prev => prev.filter(c => c.id !== id));
  }
  function updateScanConfig(id, field, value) {
    setScanConfigs(prev => prev.map(c => c.id === id ? { ...c, [field]: value } : c));
  }

  const runOneScan = useCallback(async (cfg) => {
    try {
      const res = await axios.get(`${API}/scan`, {
        params: { symbol: cfg.symbol, interval: cfg.interval, strategy: cfg.strategy, target: cfg.target, slAtr: cfg.slAtr },
        timeout: 15000,
      });
      if (res.data.success) {
        const key = `${cfg.symbol}_${cfg.interval}_${cfg.strategy}`;
        setScanResults(prev => ({ ...prev, [key]: res.data }));
        if (res.data.ltp) ltpCacheRef.current[cfg.symbol] = res.data.ltp;

        if (autoTrade && res.data.signal) {
          const sig = res.data.signal;
          const sigKey = `${cfg.symbol}_${cfg.interval}_${cfg.strategy}_${sig.type}_${sig.entryPrice}_${sig.date}`;
          if (!processedRef.current.has(sigKey)) {
            processedRef.current.add(sigKey);
            setPaperTrades(prev => [{
              id: Date.now() + Math.random(),
              time: new Date().toISOString(),
              symbol: cfg.symbol, type: sig.type,
              entry: sig.entryPrice, sl: sig.stopLoss, target: sig.target, risk: sig.risk,
              detail: sig.detail,
              scanLabel: `${cfg.symbol} ${cfg.interval} ${cfg.strategy} ${cfg.target}R`,
              status: "OPEN", exitPrice: null, exitReason: null, pnlPct: null,
            }, ...prev]);
          }
        }

        const ltp = res.data.ltp;
        if (ltp) {
          setPaperTrades(prev => prev.map(t => {
            if (t.status !== "OPEN" || t.symbol !== cfg.symbol) return t;
            if (t.type === "LONG") {
              if (ltp <= t.sl) return { ...t, status: "CLOSED", exitPrice: t.sl, exitReason: "STOP LOSS", pnlPct: round((t.sl - t.entry) / t.entry * 100) };
              if (ltp >= t.target) return { ...t, status: "CLOSED", exitPrice: t.target, exitReason: "TARGET HIT", pnlPct: round((t.target - t.entry) / t.entry * 100) };
            } else {
              if (ltp >= t.sl) return { ...t, status: "CLOSED", exitPrice: t.sl, exitReason: "STOP LOSS", pnlPct: round((t.entry - t.sl) / t.entry * 100) };
              if (ltp <= t.target) return { ...t, status: "CLOSED", exitPrice: t.target, exitReason: "TARGET HIT", pnlPct: round((t.entry - t.target) / t.entry * 100) };
            }
            return t;
          }));
        }
      }
    } catch (err) { console.warn(`Gold scan error [${cfg.symbol} ${cfg.interval}]:`, err.message); }
  }, [autoTrade]);

  useEffect(() => {
    Object.values(scanTimersRef.current).forEach(clearInterval);
    scanTimersRef.current = {};
    if (!scanningAll || scanConfigs.length === 0) return;
    for (const cfg of scanConfigs) {
      runOneScan(cfg);
      const ms = Math.max(15000, Math.min(60000, parseIntervalMs(cfg.interval)));
      scanTimersRef.current[cfg.id] = setInterval(() => runOneScan(cfg), ms);
    }
    return () => { Object.values(scanTimersRef.current).forEach(clearInterval); scanTimersRef.current = {}; };
  }, [scanningAll, scanConfigs, runOneScan]);

  async function runBacktest() {
    setLoading(true); setError(""); setResult(null);
    try {
      const res = await axios.get(`${API}/backtest`, {
        params: { symbol, interval, days, strategy, target, slAtr, qty, leverage },
        timeout: 60000,
      });
      if (res.data.success) setResult(res.data);
      else setError(res.data.message);
    } catch (err) { setError(err.response?.data?.message || err.message); }
    setLoading(false);
  }

  function exitTrade(id) {
    const trade = paperTrades.find(t => t.id === id);
    const ltp = trade ? ltpCacheRef.current[trade.symbol] : null;
    setPaperTrades(prev => prev.map(t => {
      if (t.id !== id || t.status !== "OPEN") return t;
      const pnl = t.type === "LONG" ? (ltp || t.entry) - t.entry : t.entry - (ltp || t.entry);
      return { ...t, status: "CLOSED", exitPrice: ltp || t.entry, exitReason: "MANUAL EXIT", pnlPct: round(pnl / t.entry * 100) };
    }));
  }

  function clearClosedTrades() {
    setPaperTrades(prev => prev.filter(t => t.status === "OPEN"));
  }

  const s = result?.summary;
  const openTrades = paperTrades.filter(t => t.status === "OPEN");
  const closedTrades = paperTrades.filter(t => t.status === "CLOSED");
  const totalPnl = closedTrades.reduce((a, t) => a + (t.pnlPct || 0), 0);

  return (
    <div className="five-ema">
      <h2 style={{ margin: "8px 0", fontSize: 16 }}>Gold Trading Lab (XAUT/USDT)</h2>
      <p style={{ fontSize: 12, color: "var(--dim2)", marginBottom: 12 }}>
        Tether Gold (XAUT) priced in USDT — 1 XAUT ≈ 1 troy oz physical gold. Data from Binance public API. Paper-trade mode — no real orders.
      </p>

      <div style={{ display: "flex", gap: 4, marginBottom: 12 }}>
        <button className={`btn ${mode === "scan" ? "btn-primary" : "btn-secondary"}`} onClick={() => setMode("scan")}>
          Scan &amp; Trade
        </button>
        <button className={`btn ${mode === "backtest" ? "btn-primary" : "btn-secondary"}`} onClick={() => setMode("backtest")}>
          Backtest
        </button>
      </div>

      <div className="ema-filters" style={{ flexWrap: "wrap", gap: 8 }}>
        <label className="filter-toggle">
          <span className="filter-name" style={{ marginRight: 4 }}>Symbol</span>
          <select className="ema-select" value={symbol} onChange={e => setSymbol(e.target.value)} disabled={scanningAll}>
            <option value="XAUTUSDT">XAUT/USDT (Tether Gold)</option>
          </select>
        </label>
        <label className="filter-toggle">
          <span className="filter-name" style={{ marginRight: 4 }}>Interval</span>
          <select className="ema-select" value={interval} onChange={e => setInterval_(e.target.value)} disabled={scanningAll}>
            <option value="5m">5m</option>
            <option value="15m">15m</option>
            <option value="1h">1h</option>
            <option value="4h">4h</option>
            <option value="1d">1d</option>
          </select>
        </label>
        <label className="filter-toggle">
          <span className="filter-name" style={{ marginRight: 4 }}>Strategy</span>
          <select className="ema-select" value={strategy} onChange={e => setStrategy(e.target.value)} disabled={scanningAll}>
            <option value="ema-crossover">EMA Crossover + RSI + ADX</option>
            <option value="mtf-ema">Multi-TF EMA (trend-filtered)</option>
            <option value="bb-squeeze">BB Squeeze Breakout</option>
            <option value="donchian-breakout">Donchian Breakout</option>
          </select>
        </label>
        <label className="filter-toggle">
          <span className="filter-name" style={{ marginRight: 4 }}>Target</span>
          <select className="ema-select" value={target} onChange={e => setTarget(e.target.value)}>
            <option value="1.5">1.5R</option>
            <option value="2">2R</option>
            <option value="2.5">2.5R</option>
            <option value="3">3R</option>
            <option value="4">4R</option>
            <option value="5">5R</option>
          </select>
        </label>
        <label className="filter-toggle">
          <span className="filter-name" style={{ marginRight: 4 }}>SL (ATR×)</span>
          <select className="ema-select" value={slAtr} onChange={e => setSlAtr(e.target.value)}>
            <option value="1">1.0</option>
            <option value="1.5">1.5</option>
            <option value="2">2.0</option>
            <option value="2.5">2.5</option>
          </select>
        </label>

        {mode === "backtest" && (
          <>
          <label className="filter-toggle">
            <span className="filter-name" style={{ marginRight: 4 }}>Days</span>
            <select className="ema-select" value={days} onChange={e => setDays(e.target.value)}>
              <option value="7">7</option>
              <option value="30">30</option>
              <option value="90">90</option>
              <option value="180">180</option>
              <option value="365">365</option>
            </select>
          </label>
          <label className="filter-toggle">
            <span className="filter-name" style={{ marginRight: 4 }}>Qty (oz)</span>
            <select className="ema-select" value={qty} onChange={e => setQty(e.target.value)} title="Base position size in troy ounces of gold">
              <option value="0.01">0.01</option>
              <option value="0.05">0.05</option>
              <option value="0.1">0.1</option>
              <option value="0.5">0.5</option>
              <option value="1">1</option>
            </select>
          </label>
          <label className="filter-toggle">
            <span className="filter-name" style={{ marginRight: 4 }}>Leverage</span>
            <select className="ema-select" value={leverage} onChange={e => setLeverage(e.target.value)} title="Multiplier — gold tokens are typically spot-only on Binance, leverage is hypothetical"
              style={{ color: parseInt(leverage) > 1 ? "#f59e0b" : undefined, fontWeight: parseInt(leverage) > 1 ? 700 : undefined }}>
              <option value="1">1x (spot)</option>
              <option value="2">2x</option>
              <option value="3">3x</option>
              <option value="5">5x</option>
            </select>
          </label>
          </>
        )}

        {mode === "scan" && (
          <>
            <button className={`btn ${scanningAll ? "btn-danger" : "btn-primary"}`} onClick={() => setScanningAll(s => !s)} disabled={scanConfigs.length === 0}>
              {scanningAll ? "Stop All Scans" : "Start All Scans"}
            </button>
            {scanningAll && (
              <label className={`sim-toggle ${autoTrade ? "sim-off" : "sim-on"}`} title="Auto-place paper trades on new signals">
                <input type="checkbox" checked={autoTrade} onChange={e => setAutoTrade(e.target.checked)} />
                {autoTrade ? "AUTO TRADE ON" : "AUTO TRADE OFF"}
              </label>
            )}
          </>
        )}
        {mode === "backtest" && (
          <button className="btn btn-primary" onClick={runBacktest} disabled={loading}>
            {loading ? "Running..." : "Run Backtest"}
          </button>
        )}
      </div>

      {error && <div className="scan-error">{error}</div>}

      {mode === "scan" && (
        <div style={{ marginTop: 16 }}>
          <div style={{ marginBottom: 12 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
              <h4 style={{ margin: 0, fontSize: 13, color: "var(--text2)" }}>Scan Configs ({scanConfigs.length})</h4>
              <button className="btn btn-sm btn-primary" onClick={addScanConfig} disabled={scanningAll} style={{ fontSize: 11 }}>+ Add Scan</button>
            </div>
            {scanConfigs.length === 0 && (
              <div style={{ padding: 16, background: "var(--panel3)", borderRadius: 8, textAlign: "center", color: "var(--muted2)", fontSize: 12 }}>
                No scans configured. Click "+ Add Scan" to add a strategy/timeframe combination.
              </div>
            )}
            {scanConfigs.map(cfg => {
              const key = `${cfg.symbol}_${cfg.interval}_${cfg.strategy}`;
              const sr = scanResults[key];
              return (
                <div key={cfg.id} style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 10px", background: "var(--panel3)", borderRadius: 6, marginBottom: 4, flexWrap: "wrap" }}>
                  <select className="ema-select" value={cfg.symbol} onChange={e => updateScanConfig(cfg.id, "symbol", e.target.value)} disabled={scanningAll} style={{ fontSize: 11, minWidth: 80 }}>
                    <option value="XAUTUSDT">XAUT</option>
                  </select>
                  <select className="ema-select" value={cfg.interval} onChange={e => updateScanConfig(cfg.id, "interval", e.target.value)} disabled={scanningAll} style={{ fontSize: 11, minWidth: 50 }}>
                    <option value="5m">5m</option><option value="15m">15m</option><option value="1h">1h</option><option value="4h">4h</option><option value="1d">1d</option>
                  </select>
                  <select className="ema-select" value={cfg.strategy} onChange={e => updateScanConfig(cfg.id, "strategy", e.target.value)} disabled={scanningAll} style={{ fontSize: 11, minWidth: 120 }}>
                    <option value="ema-crossover">EMA Cross+ADX</option><option value="mtf-ema">MTF EMA</option><option value="bb-squeeze">BB Squeeze</option><option value="donchian-breakout">Donchian</option>
                  </select>
                  <select className="ema-select" value={cfg.target} onChange={e => updateScanConfig(cfg.id, "target", e.target.value)} disabled={scanningAll} style={{ fontSize: 11, minWidth: 50 }}>
                    <option value="1.5">1.5R</option><option value="2">2R</option><option value="2.5">2.5R</option><option value="3">3R</option><option value="5">5R</option>
                  </select>
                  {sr && (
                    <span style={{ fontSize: 10, color: "var(--muted2)", marginLeft: 4 }}>
                      {sr.signal ? (
                        <span style={{ color: sr.signal.type === "LONG" ? "#22c55e" : "#ef4444", fontWeight: 700 }}>
                          {sr.signal.type} @ ${formatPrice(sr.signal.entryPrice)}
                        </span>
                      ) : "no signal"} | ${formatPrice(sr.ltp)}
                    </span>
                  )}
                  {!scanningAll && (
                    <button className="btn btn-sm" onClick={() => removeScanConfig(cfg.id)} style={{ fontSize: 10, padding: "2px 6px", background: "var(--tint-neg)", color: "var(--pale-neg)", border: "1px solid #7f1d1d", marginLeft: "auto" }}>X</button>
                  )}
                </div>
              );
            })}
          </div>

          {paperTrades.length > 0 && (
            <div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                <h4 style={{ margin: 0, fontSize: 13, color: "var(--text2)" }}>
                  Paper Trades ({openTrades.length} open, {closedTrades.length} closed)
                  {closedTrades.length > 0 && (
                    <span className={totalPnl >= 0 ? "positive" : "negative"} style={{ marginLeft: 8 }}>
                      Net: {totalPnl >= 0 ? "+" : ""}{totalPnl.toFixed(2)}%
                    </span>
                  )}
                </h4>
                {closedTrades.length > 0 && (
                  <button className="btn btn-sm" style={{ fontSize: 10 }} onClick={clearClosedTrades}>Clear Closed</button>
                )}
              </div>
              <div className="stocks-table-wrapper">
                <table className="stocks-table">
                  <thead>
                    <tr><th>Time</th><th>Scan</th><th>Type</th><th>Entry</th><th>SL</th><th>Target</th><th>LTP</th><th>P&L</th><th>Status</th><th>Action</th></tr>
                  </thead>
                  <tbody>
                    {paperTrades.slice(0, 50).map(t => {
                      const ltp = ltpCacheRef.current[t.symbol];
                      const unrealized = t.status === "OPEN" && ltp
                        ? (t.type === "LONG" ? (ltp - t.entry) / t.entry * 100 : (t.entry - ltp) / t.entry * 100)
                        : null;
                      return (
                        <tr key={t.id} className={t.status === "OPEN" ? "trade-open-row" : t.pnlPct >= 0 ? "trade-win" : "trade-loss"}>
                          <td style={{ fontSize: 10 }}>{new Date(t.time).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: false })}</td>
                          <td style={{ fontSize: 10, color: "var(--dim2)" }}>{t.scanLabel || t.symbol}</td>
                          <td><span className={`signal-badge ${t.type === "SHORT" ? "badge-short" : "badge-long"}`}>{t.type}</span></td>
                          <td>${formatPrice(t.entry)}</td>
                          <td className="sl">${formatPrice(t.sl)}</td>
                          <td>${formatPrice(t.target)}</td>
                          <td>{t.status === "OPEN" && ltp ? `$${formatPrice(ltp)}` : t.exitPrice ? `$${formatPrice(t.exitPrice)}` : "-"}</td>
                          <td className={((t.pnlPct || unrealized || 0) >= 0) ? "positive" : "negative"}>
                            {t.pnlPct != null ? `${t.pnlPct >= 0 ? "+" : ""}${t.pnlPct}%` : unrealized != null ? `${unrealized >= 0 ? "+" : ""}${unrealized.toFixed(2)}%` : "-"}
                          </td>
                          <td><span className={`exit-reason ${t.status === "OPEN" ? "" : "reason-" + (t.pnlPct >= 0 ? "win" : "loss")}`}>{t.exitReason || "OPEN"}</span></td>
                          <td>{t.status === "OPEN" && <button className="btn btn-sm btn-danger" onClick={() => exitTrade(t.id)} style={{ fontSize: 10, padding: "2px 6px" }}>Exit</button>}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {!scanningAll && scanConfigs.length === 0 && paperTrades.length === 0 && (
            <div style={{ marginTop: 20, textAlign: "center", color: "var(--muted2)" }}>
              <p style={{ fontSize: 12 }}>Add scan configs above, then click "Start All Scans". Each config runs independently. Signals feed into one trade log.</p>
            </div>
          )}
        </div>
      )}

      {mode === "backtest" && (
        <div>
          {s && (
            <div style={{ marginTop: 16 }}>
              <div className={`bt-money-banner ${s.totalPnlPct >= 0 ? "money-profit" : "money-loss"}`}>
                <div className="bt-money-amount">{s.netPnlUsd >= 0 ? "+" : ""}${Math.abs(s.netPnlUsd || 0).toLocaleString("en-US", {minimumFractionDigits: 2})} / ₹{Math.abs(s.netPnlInr || 0).toLocaleString("en-IN")}</div>
                <div className="bt-money-label">Net P&L post fees ({result.symbol} {result.interval}, {result.days}d, qty={qty} oz{s.leverage > 1 ? ` @ ${s.leverage}x leverage` : ""}) | {s.totalPnlPct >= 0 ? "+" : ""}{s.totalPnlPct}% | {s.totalPnlPoints >= 0 ? "+" : ""}{s.totalPnlPoints} pts</div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: 8, margin: "12px 0" }}>
                <StatCard label="Trades" value={s.totalTrades} />
                <StatCard label="Win Rate" value={`${s.winRate}%`} color={s.winRate >= 40 ? "#22c55e" : "#ef4444"} />
                <StatCard label="Wins / Losses" value={`${s.wins} / ${s.losses}`} />
                <StatCard label="Gross P&L" value={`$${s.grossPnlUsd}`} color={s.grossPnlUsd >= 0 ? "#22c55e" : "#ef4444"} />
                <StatCard label="Net P&L (USD)" value={`$${s.netPnlUsd}`} color={s.netPnlUsd >= 0 ? "#22c55e" : "#ef4444"} />
                <StatCard label="Net P&L (INR)" value={`₹${(s.netPnlInr || 0).toLocaleString("en-IN")}`} color={s.netPnlInr >= 0 ? "#22c55e" : "#ef4444"} />
                <StatCard label="Total Costs" value={`$${s.totalCostsUsd} / ₹${(s.totalCostsInr || 0).toLocaleString("en-IN")}`} color="#f59e0b" />
                <StatCard label="Profit Factor" value={s.profitFactor} color={s.profitFactor >= 1 ? "#22c55e" : "#ef4444"} />
                <StatCard label="Max Drawdown" value={`${s.maxDrawdownPct}%`} color="#ef4444" />
                <StatCard label="USD/INR Rate" value={s.inrRate} />
                <StatCard label="Avg Win" value={`${s.avgWinPct}%`} color="#22c55e" />
                <StatCard label="Avg Loss" value={`${s.avgLossPct}%`} color="#ef4444" />
              </div>

              <div className="stocks-table-wrapper" style={{ marginTop: 12 }}>
                <table className="stocks-table">
                  <thead>
                    <tr><th>#</th><th>Entry Time</th><th>Exit Time</th><th>Type</th><th>Entry</th><th>SL</th><th>Target</th><th>Exit</th><th>Reason</th><th>Gross $</th><th>Net $</th><th>Net ₹</th><th>Points</th><th>Bars</th></tr>
                  </thead>
                  <tbody>
                    {result.trades.map((t, idx) => (
                      <tr key={idx} className={t.result === "WIN" ? "trade-win" : "trade-loss"}>
                        <td>{t.id}</td>
                        <td style={{ fontSize: 10, whiteSpace: "nowrap" }}>{fmtDt(t.entryDate)}</td>
                        <td style={{ fontSize: 10, whiteSpace: "nowrap" }}>{fmtDt(t.exitDate)}</td>
                        <td><span className={`signal-badge ${t.type === "SHORT" ? "badge-short" : "badge-long"}`}>{t.type}</span></td>
                        <td>{formatPrice(t.entry)}</td>
                        <td className="sl">{formatPrice(t.sl)}</td>
                        <td>{formatPrice(t.target)}</td>
                        <td>{formatPrice(t.exitPrice)}</td>
                        <td><span className={`exit-reason reason-${t.result?.toLowerCase()}`}>{t.exitReason}</span></td>
                        <td className={(t.grossPnlUsd || 0) >= 0 ? "positive" : "negative"}>${t.grossPnlUsd}</td>
                        <td className={(t.netPnlUsd || 0) >= 0 ? "positive" : "negative"}>${t.netPnlUsd}</td>
                        <td className={(t.netPnlInr || 0) >= 0 ? "positive" : "negative"}>₹{(t.netPnlInr || 0).toLocaleString("en-IN")}</td>
                        <td className={(t.pnlPoints || 0) >= 0 ? "positive" : "negative"}>{t.pnlPoints >= 0 ? "+" : ""}{t.pnlPoints}</td>
                        <td>{t.bars || "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {!result && !loading && !error && (
            <div style={{ marginTop: 40, textAlign: "center", color: "var(--muted2)" }}>
              <h3 style={{ fontSize: 14 }}>Select parameters and click "Run Backtest"</h3>
              <p style={{ fontSize: 12 }}>Historical XAUT/USDT data from Binance.</p>
            </div>
          )}
          {loading && <div className="loading"><div className="spinner" /><p>Fetching data &amp; running backtest...</p></div>}
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, color }) {
  return (
    <div style={{ background: "var(--panel3)", borderRadius: 6, padding: "8px 12px", textAlign: "center" }}>
      <div style={{ fontSize: 10, color: "var(--dim2)", marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 14, fontWeight: 700, color: color || "var(--text2)" }}>{value}</div>
    </div>
  );
}

function formatPrice(p) {
  if (!p) return "-";
  if (p >= 1000) return p.toFixed(0);
  if (p >= 1) return p.toFixed(2);
  return p.toFixed(4);
}

function round(n) { return Math.round(n * 100) / 100; }

function fmtDt(iso) {
  if (!iso) return "-";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: false });
  } catch { return iso?.substring(0, 16) || "-"; }
}

function parseIntervalMs(interval) {
  const map = { "5m": 300000, "15m": 900000, "30m": 1800000, "1h": 3600000, "4h": 14400000, "1d": 86400000 };
  return map[interval] || 60000;
}
